// Modulo de subas: aumento porcentual de precios por lista con redondeo a
// pesos enteros (mecanica del Excel). Vista previa OBLIGATORIA antes de
// aplicar; cada suba aplicada queda registrada en el historial (auditoria).

import { api } from "../api";
import { formatearPesos } from "../../lib/money";
import type { Lista, Suba, SubaPreviewRespuesta } from "../../lib/types";

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}
function ddmmaaaa(iso: string): string {
  if (!iso) return "";
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}

export async function renderSubas(contenedor: HTMLElement): Promise<void> {
  let listas: Lista[] = [];
  let preview: SubaPreviewRespuesta | null = null;

  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina">Subas</h1>
      <p class="subtitulo">Aumento de precios por lista: % sobre el precio actual, redondeado a pesos. Los pedidos ya confirmados no cambian.</p>
    </div>

    <div class="tarjeta mb-4">
      <h2 class="mb-2 text-sm font-bold uppercase tracking-wide text-masa-600">1. Elegir listas</h2>
      <div class="mb-2 flex gap-2 text-sm">
        <button id="todas" class="btn-sec px-2 py-1 text-xs">Todas</button>
        <button id="ninguna" class="btn-sec px-2 py-1 text-xs">Ninguna</button>
      </div>
      <div id="listas" class="grid grid-cols-2 gap-1 sm:grid-cols-3"></div>
      <div class="mt-4 flex flex-wrap items-end gap-3">
        <label><span class="etiqueta">% de aumento</span>
          <input id="pct" type="number" min="0.1" step="0.1" class="campo w-32" placeholder="5" /></label>
        <label class="min-w-[14rem] flex-1"><span class="etiqueta">Notas (opcional)</span>
          <input id="notas" class="campo" placeholder="Ej: suba de harina julio" /></label>
        <button id="previsualizar" class="btn-primario">Vista previa</button>
        <span id="err" class="self-center text-sm text-red-600"></span>
      </div>
    </div>

    <div id="panelPreview"></div>

    <div class="tarjeta mt-4">
      <h2 class="mb-2 text-sm font-bold uppercase tracking-wide text-masa-600">Historial de subas</h2>
      <div id="historial" class="overflow-x-auto"></div>
    </div>`;

  const contListas = contenedor.querySelector("#listas") as HTMLElement;
  const inpPct = contenedor.querySelector("#pct") as HTMLInputElement;
  const inpNotas = contenedor.querySelector("#notas") as HTMLInputElement;
  const err = contenedor.querySelector("#err") as HTMLElement;
  const panelPreview = contenedor.querySelector("#panelPreview") as HTMLElement;
  const historial = contenedor.querySelector("#historial") as HTMLElement;

  try {
    listas = await api.get<Lista[]>("/api/listas");
    contListas.innerHTML = listas
      .map(
        (l) => `<label class="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-crema-50">
          <input type="checkbox" value="${l.id}" class="h-4 w-4 accent-masa-600" data-lista />
          <span>${esc(l.nombre)}${l.descripcion ? ` <span class="text-masa-400">— ${esc(l.descripcion)}</span>` : ""}</span>
        </label>`,
      )
      .join("");
  } catch {
    contListas.innerHTML = `<p class="text-sm text-red-600">Error al cargar listas</p>`;
  }

  const checks = () => [...contenedor.querySelectorAll<HTMLInputElement>("[data-lista]")];
  contenedor.querySelector("#todas")?.addEventListener("click", () => checks().forEach((c) => (c.checked = true)));
  contenedor.querySelector("#ninguna")?.addEventListener("click", () => checks().forEach((c) => (c.checked = false)));

  async function cargarHistorial(): Promise<void> {
    try {
      const subas = await api.get<Suba[]>("/api/subas");
      historial.innerHTML = subas.length
        ? `<table class="tabla"><thead><tr><th>Fecha</th><th>Lista</th><th class="text-right">%</th><th>Notas</th></tr></thead>
           <tbody>${subas
             .map(
               (s) => `<tr>
                <td class="whitespace-nowrap">${ddmmaaaa(s.fecha)}</td>
                <td>${esc(s.lista)}${s.lista_desc ? ` <span class="text-masa-400">— ${esc(s.lista_desc)}</span>` : ""}</td>
                <td class="text-right font-medium">+${s.porcentaje}%</td>
                <td class="text-masa-500">${esc(s.notas ?? "")}</td>
              </tr>`,
             )
             .join("")}</tbody></table>`
        : `<p class="text-sm text-masa-400">Todavia no se aplicaron subas.</p>`;
    } catch {
      historial.innerHTML = `<p class="text-sm text-red-600">Error al cargar historial</p>`;
    }
  }

  contenedor.querySelector("#previsualizar")?.addEventListener("click", async () => {
    err.textContent = "";
    panelPreview.innerHTML = "";
    preview = null;
    const ids = checks().filter((c) => c.checked).map((c) => Number(c.value));
    const pct = Number(inpPct.value);
    if (ids.length === 0) { err.textContent = "Elegí al menos una lista"; return; }
    if (!(pct > 0)) { err.textContent = "Ingresa un % mayor a 0"; return; }
    panelPreview.innerHTML = `<div class="tarjeta text-masa-400">Calculando vista previa...</div>`;
    try {
      preview = await api.post<SubaPreviewRespuesta>("/api/listas/suba", {
        listas: ids, porcentaje: pct, preview: true, notas: inpNotas.value.trim() || null,
      });
      pintarPreview(preview);
    } catch (e) {
      panelPreview.innerHTML = "";
      err.textContent = e instanceof Error ? e.message : "Error";
    }
  });

  function pintarPreview(p: SubaPreviewRespuesta): void {
    const totalPrecios = p.listas.reduce((a, l) => a + l.items.length, 0);
    const bloques = p.listas
      .map(
        (l) => `
        <details class="tarjeta mb-2 p-0" ${p.listas.length === 1 ? "open" : ""}>
          <summary class="cursor-pointer list-none px-4 py-3 text-sm font-bold uppercase tracking-wide text-masa-700">
            ${esc(l.nombre)} <span class="font-normal text-masa-400">(${l.items.length} precios)</span>
          </summary>
          <div class="max-h-72 overflow-y-auto px-4 pb-3">
            <table class="w-full text-sm">
              <thead class="text-[11px] uppercase tracking-wider text-masa-400"><tr>
                <th class="text-left font-normal">Artículo</th>
                <th class="text-right font-normal">Actual</th>
                <th class="text-right font-normal">Nuevo</th>
              </tr></thead>
              <tbody>${l.items
                .map(
                  (it) => `<tr class="border-t border-crema-100">
                    <td class="py-1 pr-2">${esc(it.descripcion)} <span class="text-xs text-masa-400">#${it.codigo}</span></td>
                    <td class="py-1 pr-2 text-right tabular-nums text-masa-500">${formatearPesos(it.viejo)}</td>
                    <td class="py-1 text-right font-bold tabular-nums">${formatearPesos(it.nuevo)}</td>
                  </tr>`,
                )
                .join("")}</tbody>
            </table>
          </div>
        </details>`,
      )
      .join("");

    panelPreview.innerHTML = `
      <div class="mb-3 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <b>Vista previa de suba +${p.porcentaje}%.</b> Se van a modificar <b>${totalPrecios} precios</b> en
        <b>${p.listas.length} lista(s)</b>. Los pedidos ya confirmados NO cambian. Revisá y confirmá abajo.
      </div>
      ${bloques}
      <div class="mt-3 flex items-center gap-3">
        <button id="aplicar" class="btn-primario">Aplicar suba +${p.porcentaje}%</button>
        <button id="cancelar" class="btn-sec">Cancelar</button>
        <span id="aplicarErr" class="text-sm text-red-600"></span>
      </div>`;

    panelPreview.querySelector("#cancelar")?.addEventListener("click", () => {
      panelPreview.innerHTML = "";
      preview = null;
    });
    panelPreview.querySelector("#aplicar")?.addEventListener("click", async () => {
      if (!preview) return;
      const aplicarErr = panelPreview.querySelector("#aplicarErr") as HTMLElement;
      const btn = panelPreview.querySelector("#aplicar") as HTMLButtonElement;
      btn.disabled = true;
      try {
        const r = await api.post<{ precios_actualizados: number; listas: number }>("/api/listas/suba", {
          listas: preview.listas.map((l) => l.lista_id),
          porcentaje: preview.porcentaje,
          preview: false,
          notas: inpNotas.value.trim() || null,
        });
        panelPreview.innerHTML = `
          <div class="rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">
            ✓ Suba aplicada: ${r.precios_actualizados} precios actualizados en ${r.listas} lista(s).
          </div>`;
        preview = null;
        inpPct.value = "";
        inpNotas.value = "";
        checks().forEach((c) => (c.checked = false));
        await cargarHistorial();
      } catch (e) {
        aplicarErr.textContent = e instanceof Error ? e.message : "Error";
        btn.disabled = false;
      }
    });
  }

  await cargarHistorial();
}
